import { OnboardingFlowType } from "../config/onboardingFlows";

export interface TestingCheckItem {
    text: string;
    /** When true, renders a collapsible detail-list toggle below this check. */
    collapsible?: boolean;
    /** Words in `text` that should be rendered in bold. */
    boldWords?: string[];
}

export interface TestingStep {
    id: string;
    title: string;
    description: string;
    celebrationNote?: string;
    nextSteps?: string[];
    retestNextSteps?: string[];
    checks: TestingCheckItem[];
    patientInfo?: string[];
    detailList?: {
        intro: string;
        items: string[];
    };
}

const hasAnswer = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;

export const getTestingSteps = (flowType: OnboardingFlowType, answers: Record<string, unknown>): TestingStep[] => {
    if (flowType === "healthcare") {
        return [
            {
                id: "greeting-office-info",
                title: "Test the greeting and office info",
                description: "We’ve set up your assistant with a best-practice greeting, name, and voice (configurable via ElevenLabs), and trained it on your website and details shared via email and meetings. Now let’s test the greeting and FAQs to ensure everything is accurate.",
                checks: [
                    { text: "The greeting, voice and assistant’s name match your preferences (all are customizable)." },
                    { text: "Office hours, location details, and Google Maps link in text messages are accurate.", collapsible: true }
                ],
                detailList: {
                    intro: "Inquire about:",
                    items: [
                        "About the Practice",
                        "Services",
                        "Braces",
                        "Cancellation Policy",
                        "Accepted Insurances",
                        "Office Hours",
                        "Pediatric Dentistry",
                        "Pricing",
                        "Billing",
                        "Payment Plans",
                        "Discounts & Promotions",
                        "Directions",
                        "Accepting New Patients",
                        "Walk-Ins"
                    ]
                }
            },
            {
                id: "new-patient",
                title: "Test a new patient appointment",
                description: "Now let’s test the new patient experience.\n\n**If your assistant isn't configured to handle new patient appointments, select the \"Complete Step\" button below to skip.**\n\nIt runs on a proven conversation flow—use a fake patient name and personal details and go all the way through booking, as this is connected to a test PMS.",
                checks: [
                    { text: "Assistant captures the correct patient and insurance info" },
                    { text: "Final booking confirmation includes all key appointment details and next steps (e.g., paperwork, X-rays)." }
                ]
            },
            {
                id: "existing-patient",
                title: "Test an existing patient scenario",
                description: "Next up, test an existing patient experience using the following info:\n\n**If your assistant isn't configured to handle existing patient appointments, select the \"Complete Step\" button below to skip.**",
                patientInfo: [
                    "If you're connected to Voicify's test PMS use the following patient info, otherwise use an existing patient from your test environment:",
                    "Vicky Voicify",
                    "(508) 111-1111",
                    "100 Main St., Boston, MA 02215",
                    "DOB: 01/01/2000"
                ],
                checks: [
                    { text: "Book a new appointment, call back to reschedule, and then cancel the appointment.", boldWords: ["Book", "reschedule", "cancel"] },
                    { text: "Make sure the cancellation policy is recited correctly." },
                    { text: "Ensure the final appointment confirmation is read-out as expected." }
                ]
            },
            {
                id: "insurance-billing",
                title: "Test insurance and billing questions",
                description: "Ask about insurance, pricing, or billing and make sure the assistant either answers correctly or transfers appropriately.",
                checks: [
                    { text: "Insurance guidance matches office policy." },
                    { text: "Billing and pricing questions are handled correctly." },
                    { text: "Transfers happen when the office wants them to. (Hang up once you hear the transfer ring.)" }
                ]
            },
            {
                id: "message-handoff",
                title: "Transfers",
                description: "The assistant is intentionally configured to transfer to your front desk team in specific scenarios. If you’re transferred, you can hang up once you hear the phone ring.",
                checks: [],
                detailList: {
                    intro: "Transfers are triggered by default when:",
                    items: [
                        "The patient indicates an emergency",
                        "The patient requests to speak with a human",
                        "The request is for an unsupported appointment type (e.g., med spa or complex procedures)",
                        "The assistant has difficulty understanding the patient (e.g., background noise)",
                        "The patient asks an unsupported question"
                    ]
                }
            },
            {
                id: "ready-to-go",
                title: "You're ready to go",
                description: "",
                celebrationNote: "You've completed the initial test PMS phase!",
                nextSteps: [
                    "Our team will review your feedback and implement updates where possible.",
                    "We'll begin connecting your assistant to your live PMS environment. Once ready, we'll reach out to schedule the next step.",
                    "After that, we'll shift focus to refining email content, scheduling blocks, and calendar setup."
                ],
                retestNextSteps: [
                    "Our team will review your latest feedback and make any remaining updates where possible.",
                    "We'll verify your assistant's current configuration and connections, then reach out with any next steps.",
                    "We'll continue refining email content, scheduling blocks, calendar setup, and other remaining details as needed."
                ],
                checks: []
            }
        ];
    }

    const steps: TestingStep[] = [
        {
            id: "greeting",
            title: "Test the greeting",
            description: "Call in and make sure the greeting, store name, hours, and common questions sound right for your location.",
            checks: [
                { text: "The greeting sounds brand-appropriate." },
                { text: "Store details and hours are accurate." },
                { text: "Common questions are answered correctly." }
            ]
        },
        {
            id: "pickup-order",
            title: "Test a pickup order",
            description: "Place a simple pickup order from start to finish and make sure the assistant handles it correctly.",
            checks: [
                { text: "The assistant captures the full pickup order correctly." },
                { text: "Modifiers and confirmations sound accurate." },
                { text: "Checkout details feel smooth and complete." }
            ]
        },
        {
            id: "upsells-deals",
            title: "Test upsells and deals",
            description: "Try an order that should trigger upsells and make sure any configured deals or promotions behave as expected.",
            checks: [
                { text: "Upsells feel natural and relevant." },
                hasAnswer(answers.hasCoupons) && answers.hasCoupons === "yes" ? { text: "Coupons or deals are applied the way you expect." } : { text: "The assistant behaves correctly even without active deals." },
                { text: "The order still feels smooth before checkout." }
            ]
        }
    ];

    if (answers.allowDelivery === "yes") {
        steps.push({
            id: "delivery-order",
            title: "Test a delivery order",
            description: "Place a delivery order and make sure delivery coverage, payment behavior, and address handling are correct.",
            checks: [
                { text: "Delivery availability is correct." },
                { text: "Payment and minimum rules match the setup." },
                { text: "Address and order confirmation sound complete." }
            ]
        });
    }

    steps.push(
        {
            id: "transfer-and-edge-cases",
            title: "Test transfers and edge cases",
            description: "Try a transfer scenario and a common question to make sure handoff and fallback behavior are correct.",
            checks: [
                { text: "Transfers reach the right store number." },
                { text: "Holiday closures and FAQ answers sound correct." },
                { text: "Fallback behavior feels reliable." }
            ]
        },
        {
            id: "ready-to-go",
            title: "You're ready to go",
            description: "",
            celebrationNote: "You've completed the initial assistant testing!",
            nextSteps: [
                "Our team will review your feedback and implement updates where possible.",
                "Once complete, we will reach out to schedule the next steps — ticket printing and live order confirmation testing.",
                "Please reach out with any questions or additional feedback!"
            ],
            retestNextSteps: [
                "Our team will review your latest feedback and make any remaining updates where possible.",
                "We'll verify your assistant's current configuration and connections, then reach out with any next steps.",
                "We'll continue refining the remaining experience and supporting workflows as needed."
            ],
            checks: []
        }
    );

    return steps;
};

export const getTestingStepCount = (flowType: OnboardingFlowType, answers: Record<string, unknown>): number => {
    return getTestingSteps(flowType, answers).length;
};
